/**
 * Human-readable description generator for configuration audit logs.
 *
 * Generates user-friendly descriptions at log time (not display time)
 * to preserve the exact context available at mutation time.
 */

import type { ActivityCategory, ActivityAction } from '@/types/userActivity';

type DescriptionGenerator = (meta: Record<string, unknown>) => string;

// ============================================================================
// Campaign Configuration Descriptions
// ============================================================================

// Helper to convert priority number to human-readable level
function priorityToLevel(priority: number | null | undefined): string {
  return priority === 1 ? 'High' : priority === 2 ? 'Medium' : priority === 3 ? 'Low' : 'Not Set';
}

const campaignDescriptions: Record<string, DescriptionGenerator> = {
  update_campaign_priority: meta => {
    const priority = meta['priority'] as number | null;
    const oldPriority = meta['old_priority'] as number | null | undefined;
    const campaignNumber = meta['campaign_number'] as string;
    const newLevel = priorityToLevel(priority);
    const oldLevel = priorityToLevel(oldPriority);

    if (oldPriority !== undefined) {
      return `Changed skill level from ${oldLevel} to ${newLevel} for campaign ${campaignNumber}`;
    }
    return `Set skill level to ${newLevel} for campaign ${campaignNumber}`;
  },

  toggle_mobile_eligibility: meta => {
    const eligible = meta['eligible'] as boolean;
    const campaignNumber = meta['campaign_number'] as string;
    return `${eligible ? 'Enabled' : 'Disabled'} mobile servicing for campaign ${campaignNumber}`;
  },

  update_campaign_status: meta => {
    const status = meta['status'] as string;
    const oldStatus = meta['old_status'] as string | undefined;
    const campaignNumber = meta['campaign_number'] as string;

    if (oldStatus) {
      return `Changed campaign ${campaignNumber} status from ${oldStatus} to ${status}`;
    }
    return `Set campaign ${campaignNumber} status to ${status}`;
  },

  reset_mobile_eligibility: () => {
    return 'Reset all campaigns to dealership-only servicing';
  },

  sync_campaigns: () => {
    return 'Synced campaigns from database';
  }
};

// ============================================================================
// Technician Configuration Descriptions
// ============================================================================

const technicianDescriptions: Record<string, DescriptionGenerator> = {
  create_technician: meta => {
    const name = (meta['technician_name'] as string) || 'Unknown';
    return `Created technician "${name}"`;
  },

  update_technician: meta => {
    const name = (meta['technician_name'] as string) || 'Unknown';
    const fieldsUpdated = meta['fields_updated'] as string[] | undefined;
    const changes = meta['changes'] as Record<string, { old: unknown; new: unknown }> | undefined;

    // If we have detailed changes, show them
    if (changes && Object.keys(changes).length > 0) {
      const changeDescriptions = Object.entries(changes).map(
        ([field, { old: oldVal, new: newVal }]) => {
          const fieldName = field.replace(/_/g, ' ');
          if (field === 'skill_level') {
            return `skill level from ${priorityToLevel(oldVal as number)} to ${priorityToLevel(newVal as number)}`;
          }
          if (field === 'is_active') {
            return oldVal ? 'deactivated' : 'activated';
          }
          return `${fieldName} from "${oldVal}" to "${newVal}"`;
        }
      );
      return `Updated ${changeDescriptions.join(', ')} for technician "${name}"`;
    }

    if (fieldsUpdated && fieldsUpdated.length > 0) {
      const fieldNames = fieldsUpdated.map(f => f.replace(/_/g, ' ')).join(', ');
      return `Updated ${fieldNames} for technician "${name}"`;
    }
    return `Updated technician "${name}"`;
  },

  delete_technician: meta => {
    const name = (meta['technician_name'] as string) || 'Unknown';
    return `Deleted technician "${name}"`;
  },

  update_work_schedule: meta => {
    const name = (meta['technician_name'] as string) || 'Unknown';
    return `Updated work schedule for technician "${name}"`;
  }
};

// ============================================================================
// Dealer Configuration Descriptions
// ============================================================================

const dealerDescriptions: Record<string, DescriptionGenerator> = {
  toggle_dealer_inclusion: meta => {
    const isBulk = meta['bulk'] as boolean | undefined;
    const isIncluded = meta['is_included'] as boolean;
    const wasIncluded = meta['was_included'] as boolean | undefined;
    const dealerName = meta['dealer_name'] as string | undefined;
    const count = meta['count'] as number | undefined;

    if (isBulk && count) {
      return `${isIncluded ? 'Included' : 'Excluded'} ${count} dealers in bulk`;
    }

    // Show state change if we know the old state
    if (wasIncluded !== undefined && wasIncluded !== isIncluded) {
      return `Changed dealer "${dealerName || 'Unknown'}" from ${wasIncluded ? 'included' : 'excluded'} to ${isIncluded ? 'included' : 'excluded'}`;
    }
    return `${isIncluded ? 'Included' : 'Excluded'} dealer "${dealerName || 'Unknown'}"`;
  },

  update_dealer_notes: meta => {
    const dealerName = meta['dealer_name'] as string;
    const hadNotes = meta['had_notes'] as boolean | undefined;
    if (hadNotes === false) {
      return `Added notes for dealer "${dealerName}"`;
    }
    return `Updated notes for dealer "${dealerName}"`;
  },

  sync_dealers: () => {
    return 'Synced dealers from database';
  }
};

// ============================================================================
// Main Generator Function
// ============================================================================

const categoryTemplates: Record<string, Record<string, DescriptionGenerator>> = {
  campaign_config: campaignDescriptions,
  technician_config: technicianDescriptions,
  dealer_config: dealerDescriptions
};

/**
 * Generates a human-readable description for an activity log entry.
 *
 * @param category - The activity category (campaign_config, technician_config, dealer_config)
 * @param action - The specific action performed
 * @param metadata - Additional context data for the action
 * @returns A human-readable description string
 */
export function generateHumanDescription(
  category: ActivityCategory,
  action: ActivityAction,
  metadata: Record<string, unknown>
): string {
  const templates = categoryTemplates[category];
  if (!templates) {
    // Fallback: Convert action to sentence case
    return action.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
  }

  const generator = templates[action];
  if (!generator) {
    // Fallback: Convert action to sentence case
    return action.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
  }

  try {
    return generator(metadata);
  } catch {
    // Fallback on any error
    return action.replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase());
  }
}
